import React from 'react';
import LegalLayout, { Email, Fill } from './LegalLayout';
import { OWNER } from '../../config/legal';

// Privacy policy (GDPR art. 13) and use of browser storage (LSSI-CE art. 22).
// Keep it in step with what the code actually stores: supabase/migrations and
// the Edge Functions are the source of truth.
const Privacidad = () => (
  <LegalLayout
    title="Política de privacidad"
    intro="Qué datos tratamos, para qué, con quién y cuánto tiempo. Recogemos lo mínimo para darte el servicio y no vendemos ni cedemos datos a nadie para publicidad."
  >
    <h2>Responsable</h2>
    <p>
      <Fill value={OWNER.name} label="nombre y apellidos" /> (NIF <Fill value={OWNER.nif} label="NIF" />),{' '}
      <Fill value={OWNER.address} label="domicilio" />. Para cualquier cosa sobre tus datos: <Email />.
    </p>

    <h2>Qué datos tratamos y para qué</h2>

    <h3>Comprobación gratuita</h3>
    <p>
      El nombre del negocio, la ciudad, a qué se dedica y, si la das, su web. Los usamos para formular la pregunta a la
      IA y enseñarte el resultado. Suelen ser datos de empresa, pero si tu negocio lleva tu nombre pueden ser datos
      personales. Guardamos la comprobación para reutilizarla si alguien consulta el mismo negocio en los 7 días
      siguientes y para mejorar el servicio.
    </p>
    <ul>
      <li>
        <strong>Base legal:</strong> darte el servicio que pides (art. 6.1.b del RGPD).
      </li>
      <li>
        <strong>Tu IP:</strong> no la guardamos. Guardamos una huella irreversible de ella (un hash con una clave
        secreta) para limitar el número de comprobaciones por persona y evitar abusos (interés legítimo, art. 6.1.f).
      </li>
    </ul>

    <h3>Informe completo y pago</h3>
    <p>
      Si pagas el informe, el pago lo gestiona Stripe: los datos de tu tarjeta van directamente a Stripe y nunca pasan
      por nosotros. Stripe nos informa de tu email, del importe y de si el pago se ha completado. El informe se guarda
      para que puedas volver a verlo en su enlace.
    </p>
    <ul>
      <li>
        <strong>Base legal:</strong> el contrato de compra (art. 6.1.b) y las obligaciones fiscales y contables (art.
        6.1.c).
      </li>
      <li>
        <strong>Importante:</strong> cualquiera que tenga el enlace de tu informe puede verlo. Es largo e imposible de
        adivinar, pero compártelo solo con quien quieras.
      </li>
    </ul>

    <h3>Aviso del seguimiento mensual</h3>
    <p>
      Si nos dejas tu email al final del informe para que te avisemos cuando exista el seguimiento mensual, lo usamos
      solo para eso.
    </p>
    <ul>
      <li>
        <strong>Base legal:</strong> tu consentimiento (art. 6.1.a), que puedes retirar cuando quieras escribiéndonos.
      </li>
    </ul>

    <h3>Seguimiento mensual (en pruebas)</h3>
    <p>
      Si activas el seguimiento con un código de prueba, guardamos tu email y los datos del negocio del informe. Cada
      mes hacemos un informe nuevo del negocio y te enviamos por email el resultado comparado con el anterior, con el
      enlace para verlo entero. Solo te escribimos para esto.
    </p>
    <ul>
      <li>
        <strong>Base legal:</strong> darte el servicio que has activado (art. 6.1.b).
      </li>
      <li>
        <strong>Baja:</strong> cada email lleva un enlace para darte de baja con un clic. Desde ese momento no hacemos
        más informes ni te escribimos.
      </li>
    </ul>

    <h3>Negocios a los que podemos presentar el servicio</h3>
    <p>
      Para dar a conocer webspector a negocios locales y hacer estudios por ciudad, tomamos de OpenStreetMap (una base
      de datos abierta) el nombre, la actividad, la dirección, la web y el teléfono que los negocios publican. No
      guardamos direcciones de email ni enviamos correos comerciales a quien no los ha pedido. De los estudios solo
      publicamos cifras agregadas, nunca el resultado de un negocio concreto.
    </p>
    <p>
      Apuntamos si le hemos escrito una carta o le hemos visitado, para no repetirlo. Si haces la comprobación o un
      informe de un negocio que está en esa lista, o con un código que le dimos, lo enlazamos con él: así sabemos si
      nuestra carta le llegó y no volvemos a escribirle.
    </p>
    <ul>
      <li>
        <strong>Base legal:</strong> nuestro interés legítimo en presentar el servicio a negocios (art. 6.1.f). Si tu
        negocio aparece y prefieres que no, escríbenos a <Email /> y lo borramos.
      </li>
    </ul>

    <h3>Códigos de invitación y contacto</h3>
    <p>
      Si te damos un código de invitación, podemos anotar para quién es. Si nos escribes, usamos tu email y lo que nos
      cuentes para responderte.
    </p>

    <h2>Cuánto tiempo los guardamos</h2>
    <ul>
      <li>Comprobaciones e informes: mientras sigan siendo útiles para el servicio, o hasta que nos pidas borrarlos.</li>
      <li>Datos del pago: el tiempo que exige la normativa fiscal y mercantil (hasta 6 años).</li>
      <li>Email del aviso del seguimiento: hasta que lancemos el servicio y te avisemos, o hasta que nos pidas borrarlo.</li>
      <li>Datos públicos de negocios para presentarles el servicio: mientras sean útiles, o hasta que nos pidas borrarlos.</li>
      <li>
        Email del seguimiento mensual: mientras esté activo. Cuando te das de baja o termina la prueba dejamos de
        usarlo, y lo borramos si nos lo pides.
      </li>
    </ul>

    <h2>Con quién los compartimos</h2>
    <p>Solo con los proveedores que necesitamos para que la web funcione, que tratan los datos por encargo nuestro:</p>
    <ul>
      <li>
        <strong>Supabase</strong>: base de datos y funciones del servicio, en servidores de la Unión Europea (París).
      </li>
      <li>
        <strong>Vercel</strong>: alojamiento de la web. Es una empresa estadounidense; las transferencias se hacen con las
        garantías que prevé el RGPD.
      </li>
      <li>
        <strong>Stripe</strong>: cobro de los pagos con tarjeta.
      </li>
      <li>
        <strong>Xiaomi (plataforma de IA MiMo)</strong>: recibe el nombre, la ciudad, el sector y la web del negocio
        para formular las preguntas y analizar las respuestas. Nunca le enviamos emails, datos de pago ni tu IP. Los
        procesa en servidores de Singapur, un país sin decisión de adecuación de la Comisión Europea, así que es una
        transferencia internacional; se limita a esos datos del negocio y se rige por la{' '}
        <a href="https://privacy.mi.com/XiaomiMiMoPlatform/en_GB/" rel="noreferrer" target="_blank">
          política de privacidad de Xiaomi MiMo
        </a>
        .
      </li>
      <li>
        <strong>Resend</strong>: envío de los emails del seguimiento mensual. Recibe tu email y el contenido del
        mensaje. Es una empresa estadounidense; los envíos salen de servidores en la Unión Europea (Irlanda) y las
        transferencias se hacen con las garantías que prevé el RGPD.
      </li>
      <li>
        <strong>Hostalia</strong>: correo electrónico del dominio, para los mensajes que nos envías.
      </li>
    </ul>
    <p>No cedemos datos a terceros salvo obligación legal.</p>

    <h2>Tus derechos</h2>
    <p>
      Puedes pedirnos acceder a tus datos, corregirlos, borrarlos, oponerte a su uso, limitarlo o recibirlos en un
      formato para llevarlos a otro servicio. Escríbenos a <Email /> y te responderemos en un mes como mucho. Si no
      quedas satisfecho, puedes reclamar ante la Agencia Española de Protección de Datos (
      <a href="https://www.aepd.es" rel="noreferrer" target="_blank">
        www.aepd.es
      </a>
      ).
    </p>

    <h2>Cookies y almacenamiento en tu navegador</h2>
    <p>
      No usamos cookies ni herramientas de analítica o publicidad, y las tipografías se sirven desde nuestra propia
      web, no desde Google. Solo guardamos en tu navegador lo imprescindible para funciones que tú usas:
    </p>
    <ul>
      <li>Los pasos del plan de acción que marcas como hechos, para que sigan marcados al volver.</li>
      <li>
        El resultado de tu última comprobación, mientras la pestaña esté abierta, para no perderlo si cancelas el pago.
      </li>
    </ul>
    <p>
      Al pagar, pasas a la página de pago de Stripe, que usa sus propias cookies para procesar el pago y prevenir el
      fraude, según su política de privacidad.
    </p>

    <h2>Cambios</h2>
    <p>Si cambiamos esta política, publicaremos aquí la nueva versión con su fecha.</p>
  </LegalLayout>
);

export default Privacidad;
